import React, { useState, useMemo, useEffect } from 'react';
import { ModalHeader, ModalBody, ModalFooter, Accordion, AccordionItem, Select, SelectItem, Input } from '@nextui-org/react';
import { CtyButton } from '../button/CtyButton';
import CtySpinner from '../spinner/CtySpinner';
import { conversationHistoryTimezone, formatCoverageDate } from '../utils/dateutils';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';

export interface UsageEventRequestMetadata {
    asn?: string;
    city?: string;
    origin?: string;
    region?: string;
    country?: string;
    referer?: string;
    platform?: string;
    timestamp?: string;
    ip_address?: string;
    user_agent?: string;
    device_type?: string;
    postal_code?: string;
    region_name?: string;
}

export interface UsageEventData {
    event_id: string;
    event_type: string;
    aiworker_usage_id: string;
    aiworker_id: string;
    aiworker_name: string;
    created_at: string;
    updated_at: string;
    updated_by: string;
    created_by: string;
    request_metadata?: UsageEventRequestMetadata;
}

export interface UsageEventsViewProps {
    events: UsageEventData[];
    isLoading: boolean;
    title?: string;
    onClose?: () => void;
    emptyMessage?: string;
    noFilterResultsMessage?: string;
    showCloseButton?: boolean;
}

const formatValue = (value: unknown): string => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
};

const UsageEventsView: React.FC<UsageEventsViewProps> = ({
    events,
    isLoading,
    title,
    onClose,
    emptyMessage,
    noFilterResultsMessage,
    showCloseButton = true,
}) => {
    const { t } = useTranslation();
    const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set(['event-0']));
    const [selectedUser, setSelectedUser] = useState<string>('all');
    const [selectedDate, setSelectedDate] = useState<string>('');

    useEffect(() => {
        if (events.length > 0) {
            setSelectedKeys(new Set(['event-0']));
        }
    }, [events.length]);

    const getEventTimestamp = (event: UsageEventData): string => {
        if (event.created_at) {
            return conversationHistoryTimezone(event.created_at);
        }
        if (event.request_metadata?.timestamp) {
            return conversationHistoryTimezone(event.request_metadata.timestamp);
        }
        return '-';
    };

    const getEventDateOnly = (event: UsageEventData): string | null => {
        const timestamp = getEventTimestamp(event).split(',')[0];
        if (!timestamp) return null;
        return formatCoverageDate(timestamp);
    };

    const uniqueUsers = useMemo(() => {
        const names = events
            .map((e) => e.created_by)
            .filter(Boolean) as string[];
        return Array.from(new Set(names));
    }, [events]);

    const filteredEvents = useMemo(() => {
        return events.filter((event) => {
            if (selectedUser !== 'all' && event.created_by !== selectedUser) {
                return false;
            }
            const dateOnly = getEventDateOnly(event);
            if (formatCoverageDate(selectedDate) && (!dateOnly || dateOnly !== formatCoverageDate(selectedDate))) {
                return false;
            }
            return true;
        });
    }, [events, selectedUser, selectedDate]);

    const renderEventDetails = (event: UsageEventData) => {
        const eventFields = [
            { key: 'aiworker_name', label: t('AI Worker Name'), value: event.aiworker_name },
            { key: 'event_type', label: t('Event Type'), value: event.event_type },
        ];

        const metadataFields = event.request_metadata
            ? [
                  { key: 'ip_address', label: t('IP Address'), value: event.request_metadata.ip_address },
                  { key: 'user_agent', label: t('User Agent'), value: event.request_metadata.user_agent },
                  { key: 'device_type', label: t('Device Type'), value: event.request_metadata.device_type },
                  { key: 'platform', label: t('Platform'), value: event.request_metadata.platform },
                  { key: 'country', label: t('Country'), value: event.request_metadata.country },
                  { key: 'region', label: t('Region'), value: event.request_metadata.region },
                  { key: 'region_name', label: t('Region Name'), value: event.request_metadata.region_name },
                  { key: 'city', label: t('City'), value: event.request_metadata.city },
                  { key: 'postal_code', label: t('Postal Code'), value: event.request_metadata.postal_code },
                  { key: 'asn', label: t('ASN'), value: event.request_metadata.asn },
                  { key: 'origin', label: t('Origin'), value: event.request_metadata.origin },
                  { key: 'referer', label: t('Referer'), value: event.request_metadata.referer },
                  {
                      key: 'timestamp',
                      label: t('Timestamp'),
                      value: event.request_metadata.timestamp
                          ? conversationHistoryTimezone(event.request_metadata.timestamp)
                          : '-',
                  },
              ]
            : [];

        return (
            <div className="space-y-6">
                <div className="bg-default-100 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-default-600 mb-3 flex items-center gap-2">
                        <Icon icon="mdi:information-outline" className="w-4 h-4" />
                        {t('Event Information')}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {eventFields.map((field) => (
                            <div key={field.key} className="flex flex-col">
                                <span className="text-xs font-medium text-default-500 mb-1">{field.label}</span>
                                <span className="text-sm text-foreground break-words">{formatValue(field.value)}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {event.request_metadata && Object.keys(event.request_metadata).length > 0 && (
                    <div className="bg-default-100 rounded-lg p-4">
                        <h3 className="text-sm font-semibold text-default-600 mb-3 flex items-center gap-2">
                            <Icon icon="mdi:database-outline" className="w-4 h-4" />
                            {t('Request Metadata')}
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {metadataFields.map((field) => (
                                <div key={field.key} className="flex flex-col">
                                    <span className="text-xs font-medium text-default-500 mb-1">{field.label}</span>
                                    <span className="text-sm text-foreground break-words">{formatValue(field.value)}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const displayTitle = title ?? t('AI Workers Usage Events');
    const displayEmptyMessage = emptyMessage ?? t('No events found');
    const displayNoFilterMessage = noFilterResultsMessage ?? t('No events found for the selected filters');

    return (
        <>
            <ModalHeader className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                    <Icon icon="mdi:history" className="w-5 h-5 text-primary" />
                    <h2 className="text-xl font-semibold">{displayTitle}</h2>
                </div>
                {events.length > 0 && (
                    <>
                        <span className="text-sm text-default-600">
                            {t('Total Events')}: {events.length}
                        </span>
                        <div className="mt-3 flex flex-col gap-3 md:flex-row md:items-end">
                            <div className="w-[30%]">
                                <Select
                                    size="sm"
                                    selectedKeys={new Set([selectedUser])}
                                    onSelectionChange={(keys) => {
                                        const key = Array.from(keys)[0] as string;
                                        setSelectedUser(key ?? 'all');
                                    }}
                                >
                                    <SelectItem key="all" value="all">
                                        {t('All Users')}
                                    </SelectItem>
                                    {uniqueUsers.map((user) => (
                                        <SelectItem key={user} value={user}>
                                            {user}
                                        </SelectItem>
                                    ))}
                                </Select>
                            </div>
                            <div className="flex flex-col gap-2 md:flex-row md:w-[20%]">
                                <Input
                                    size="sm"
                                    type="date"
                                    value={selectedDate}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                />
                            </div>
                        </div>
                    </>
                )}
            </ModalHeader>
            <ModalBody>
                {isLoading ? (
                    <div className="flex items-center justify-center py-8">
                        <CtySpinner size="md" />
                    </div>
                ) : events.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-default-500">
                        <Icon icon="mdi:information-outline" className="w-12 h-12 mb-2" />
                        <p>{displayEmptyMessage}</p>
                    </div>
                ) : filteredEvents.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-default-500">
                        <Icon icon="mdi:filter-off-outline" className="w-12 h-12 mb-2" />
                        <p>{displayNoFilterMessage}</p>
                    </div>
                ) : (
                    <Accordion
                        selectedKeys={selectedKeys}
                        onSelectionChange={(keys) => setSelectedKeys(keys as Set<string>)}
                        variant="splitted"
                        isCompact
                        className="gap-2"
                    >
                        {filteredEvents.map((event, index) => {
                            const eventTimestamp = getEventTimestamp(event);
                            return (
                                <AccordionItem
                                    key={event.event_id || `event-${index}`}
                                    aria-label={`${t('Event')} ${index + 1}`}
                                    title={
                                        <div className="flex items-center gap-2">
                                            <Icon icon="mdi:calendar-clock" className="w-4 h-4 text-primary" />
                                            <span className="font-medium">
                                                {event.event_type} - {event.created_by}
                                            </span>
                                            <span className="text-xs text-default-500 ml-2">{eventTimestamp}</span>
                                        </div>
                                    }
                                >
                                    <div className="pt-2">{renderEventDetails(event)}</div>
                                </AccordionItem>
                            );
                        })}
                    </Accordion>
                )}
            </ModalBody>
            {showCloseButton && onClose && (
                <ModalFooter>
                    <CtyButton variant="bordered" onPress={onClose} className="w-full">
                        {t('Close')}
                    </CtyButton>
                </ModalFooter>
            )}
        </>
    );
};

export default UsageEventsView;
